import type { IntlKey } from 'locales';
import type { MapDispatchToProps, MapStateToPropsFactory } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { secondaryMarketOrderBlotterFilterByOrderId } from 'state/blotters/secondaryMarketOrderBlotter';
import type { LimitOrderInputFormActionsFunctions } from 'state/secondaryMarket/limit-order/form/input/inputForm.actions';
import type { LimitOrderSelectors } from 'state/secondaryMarket/limit-order/limitOrder.selector';
import type { LimitOrderThunks } from 'state/secondaryMarket/limit-order/limitOrder.thunk';
import type { LiveInputFormActionFunctions } from 'state/secondaryMarket/live/form/input/inputForm.actions';
import type { LiveSelectors } from 'state/secondaryMarket/live/live.selector';
import type { LiveThunks } from 'state/secondaryMarket/live/live.thunk';
import type { AmendSelectors } from 'state/secondaryMarket/shared/amend/amend.selector';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import type { KycSelectors } from 'state/secondaryMarket/shared/kyc/kyc.selector';
import { getUserFrontRights } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { TabLoadingStatus } from 'state/workspaces/tabs/tabs.model';
import { getTabLoadingStatus } from 'state/workspaces/tabs/tabs.selector';
import type { LoadingStatus } from 'utils/status';

import type { KycDetail, KycScoreStatus } from '../../../../../state/secondaryMarket/shared/kyc/kyc.model';
import type { SecondaryMarketPostEventTabType } from '../../../../../state/workspaces/model';

type OwnProps = Readonly<{
  tabId: string;
  tabType: SecondaryMarketPostEventTabType;
  title: IntlKey;
  applyLabelKey: IntlKey;
  readyLabelKey: IntlKey;
  notReadyLabelKey: IntlKey;
}>;

type StateProps = Readonly<{
  isLoading: boolean;
  canApply: boolean;
  comment: string | undefined;
  kycScore: KycScoreStatus | undefined;
  kycDetails: readonly KycDetail[] | undefined;
  applyStatus: LoadingStatus;
  validationErrors: readonly SecondaryMarketFormError[];
  backendReports: readonly SecondaryMarketFormError[];
  errorMessage: string | undefined;
}>;

type DispatchProps = Readonly<{
  onApply: () => void;
  onDone: () => void;
  onReset: () => void;
  onCommentChanged: (value: string) => void;
  onNavigateToOrdersBlotter: () => void;
}>;

export type AmendSidePanelProps = OwnProps & StateProps & DispatchProps;

export function connectAmendSidePanelFactory(
  selectors: LiveSelectors | LimitOrderSelectors,
  kycSelectors: KycSelectors,
  amendSelectors: AmendSelectors,
  thunks: LiveThunks | LimitOrderThunks,
  inputFormActions: LiveInputFormActionFunctions | LimitOrderInputFormActionsFunctions,
) {
  const getBackendReports = selectors.makeGetBackendReports();
  const mapStateToProps: MapStateToPropsFactory<StateProps, OwnProps, AppState> = () => {
    return (state, { tabId }) => {
      const isLoading = getTabLoadingStatus(state, tabId) === TabLoadingStatus.Loading;
      const canApply = getUserFrontRights(state).secondaryTradeEnabled;
      const formErrors = selectors.getAmendValidationErrors(state, tabId) as SecondaryMarketFormError[];
      const backendReports = getBackendReports(state, tabId) as SecondaryMarketFormError[];

      const kycScore = kycSelectors.getKycStatusScore(state, tabId);
      const kycDetails = kycSelectors.getKycStatusDetails(state, tabId);
      const comment = selectors.getFormComment(state, tabId);
      const applyStatus = amendSelectors.getAmendedOrderStatus(state, tabId);
      const errorMessage = amendSelectors.getErrorMessage(state, tabId);
      const backendErrors = amendSelectors.getOrderValidationErrors(state, tabId);

      return {
        canApply,
        comment,
        kycScore,
        kycDetails,
        isLoading,
        applyStatus,
        validationErrors: applyStatus === 'idle' ? formErrors : backendErrors,
        errorMessage,
        backendReports,
      };
    };
  };
  const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = (dispatch: ThunkDispatch, { tabId }) => ({
    onApply: () => {
      logAnalytics.trackEvent('Secondary market', 'Apply amend');
      dispatch(thunks.amendRequestedThunk(tabId));
    },
    onCommentChanged: value => {
      dispatch(inputFormActions.formFieldChanged(tabId, 'comment', value));
    },
    onDone: () => {
      dispatch(thunks.amendResetTabThunk(tabId));
    },
    onReset: () => {
      dispatch(thunks.amendResetTabThunk(tabId));
    },
    onNavigateToOrdersBlotter: () => {
      dispatch(secondaryMarketOrderBlotterFilterByOrderId(tabId));
    },
  });
  return { mapStateToProps, mapDispatchToProps };
}
